import WebApp from '@twa-dev/sdk';
import { useTranslation } from 'react-i18next';

import { useAnalyticsService } from '@/app/providers/analytics-service/hooks';

import { useUser } from '@/entities/user/model/user.store';

import { getGameByKey } from '@/shared/api/api';
import { makeShareEvent } from '@/shared/services/analytics/events/share-event';

import { getShareMessageId } from './api';
import { generateButtonUrl } from './lib/generate-button-url';
import { mergeSharingData } from './lib/merge-sharing-data';
import { useSharingData } from './lib/use-sharing-data';
import { MediaType, type ShareData, type ShareVariants } from './types';

export const useShare = (source: string, shareVariant: ShareVariants = 'REFERRAL') => {
  const analyticsService = useAnalyticsService();
  const { t } = useTranslation();

  const user = useUser();

  const defalutSharingData = useSharingData(shareVariant, source);

  const share = async (customSharingData?: Partial<ShareData>) => {
    if (!user) return;

    let sharingData: ShareData;

    // Обрабатываем игры отдельно
    if (shareVariant === 'GAME' && customSharingData?.gameKey) {
      try {
        const game = await getGameByKey(customSharingData.gameKey);

        const hasDescription = !!game.mpDescription.trim();
        const playWithMeText = t('play with me on Telegram!');
        const playWithMeTextCapitalized = playWithMeText.replace(/^\p{L}/u, (c) =>
          c.toUpperCase()
        );

        const text = hasDescription
          ? `${game.title}: ${game.mpDescription}\n\n${playWithMeTextCapitalized} 🕹️`
          : `${game.title} - ${playWithMeText}`;

        const buttonUrl = generateButtonUrl({
          ref: user.telegramId,
          src: source,
        });

        const mediaId = game.iconUrl;

        sharingData = {
          telegramId: user.telegramId,
          mediaId,
          mediaType: MediaType.ANIMATION,
          text,
          buttonText: `${t('Play')}${hasDescription ? '' : ' 🕹️'}`,
          buttonUrl,
          gameKey: customSharingData.gameKey,
        };
      } catch (err) {
        console.error('Ошибка при получении данных игры:', err);
        return;
      }
    } else {
      if (!defalutSharingData) return;
      sharingData = mergeSharingData(defalutSharingData, customSharingData);
    }

    void getShareMessageId({
      ...sharingData,
    }).then((preparedInlineMessageId) => {
      WebApp.shareMessage(preparedInlineMessageId, (hasSent) => {
        const e = makeShareEvent(source, hasSent);
        analyticsService.send(e);
      });
    });
  };

  return share;
};
